import { RefreshCw } from "lucide-react";
import { useMemo, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  DisplayOptions,
  type DisplayProperty
} from "@/components/ui/display-options";
import {
  ChipMenu,
  ListSearch,
  ListToolbar
} from "@/components/ui/list-toolbar";

import {
  SORT_OPTIONS,
  type AssignmentFilter,
  type SortFilter
} from "../clientsListFilters";
import { ClientsQuickFilters } from "./ClientsQuickFilters";

type PracticeTeamMember = { uid?: string; label: string; email?: string };

const UNASSIGNED = "__unassigned";

type Props = {
  searchInput: string;
  onSearchChange: (value: string) => void;
  sortFilter: SortFilter;
  onSortChange: (value: SortFilter) => void;
  assignmentFilter: AssignmentFilter;
  onAssignmentFilterChange: (value: AssignmentFilter) => void;
  assignedUserUidFilter: string;
  onAssignedUserUidFilterChange: (value: string) => void;
  practiceTeamMemberOptions: PracticeTeamMember[];
  quickFilters: Parameters<typeof ClientsQuickFilters>[0];
  /** Display's Properties: the table's optional columns. */
  columns: DisplayProperty[];
  onColumnChange: (id: string, visible: boolean) => void;
  /** Why some columns start hidden, under the properties. */
  columnsNote?: string;
  onRefresh: () => void;
};

/**
 * Search, the Assigned chip and the quick filters over the clients table,
 * then one Display button (ordering and visible columns) and refresh.
 */
export const ClientsTableToolbar: FC<Props> = ({
  searchInput,
  onSearchChange,
  sortFilter,
  onSortChange,
  assignmentFilter,
  onAssignmentFilterChange,
  assignedUserUidFilter,
  onAssignedUserUidFilterChange,
  practiceTeamMemberOptions,
  quickFilters,
  columns,
  onColumnChange,
  columnsNote,
  onRefresh
}) => {
  const assignedOptions = useMemo(
    () => [
      { value: UNASSIGNED, label: "Unassigned" },
      ...practiceTeamMemberOptions.flatMap((m) => {
        const uid = (m.uid ?? "").trim();
        return uid ? [{ value: uid, label: m.label }] : [];
      })
    ],
    [practiceTeamMemberOptions]
  );
  const assignedValue =
    assignedUserUidFilter.trim() ||
    (assignmentFilter === "unassigned" ? UNASSIGNED : "");

  return (
    <ListToolbar className="flex-nowrap">
      <ListSearch
        value={searchInput}
        onValueChange={onSearchChange}
        placeholder="Search clients…"
        aria-label="Search Clients"
        className="w-50 shrink-0"
      />
      <ChipMenu
        label="Assigned"
        anyLabel="Anyone"
        options={assignedOptions}
        value={assignedValue}
        onValueChange={(v) => {
          if (v === UNASSIGNED) {
            onAssignedUserUidFilterChange("");
            onAssignmentFilterChange("unassigned");
            return;
          }
          onAssignmentFilterChange("all");
          onAssignedUserUidFilterChange(v);
        }}
        scroll
      />
      <ClientsQuickFilters {...quickFilters} />
      <span className="flex-1" />
      <DisplayOptions
        ordering={{
          options: SORT_OPTIONS,
          value: sortFilter,
          onValueChange: (v) => onSortChange(v as SortFilter)
        }}
        properties={columns}
        onPropertyChange={onColumnChange}
        note={columnsNote}
      />
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Refresh"
        onClick={onRefresh}
      >
        <RefreshCw />
      </Button>
    </ListToolbar>
  );
};
